<script>
  import { createEventDispatcher } from "svelte";
  import { Icon, PaperAirplane } from "svelte-hero-icons";
  import { execution, liveStore } from "../store";
  import { persisted } from "$lib/utils/persisted-stores";
  import DropdownMenu from "$lib/Elements/DropdownMenu.svelte";
  import Button from "$lib/Elements/Button.svelte";
  import TextInput from "$lib/Inputs/TextInput.svelte";
  import SelectInput from "$lib/Inputs/SelectInput.svelte";
  import CheckboxInput from "$lib/Inputs/CheckboxInput.svelte";
  import DurationInput from "$lib/Inputs/DurationInput.svelte";

  export let apps = [];
  export let devices = [];
  export let availableEntrypoints;

  // Persisted state - remembered across sessions
  // Using v2 suffix to avoid conflicts with old storage format
  const persistedUserTag = persisted("execute_all_user_tag", null);
  const persistedDeviceSelections = persisted("execute_all_device_selections_v2", {});
  const persistedBuildId = persisted("execute_all_build_id", null);
  const persistedGodMode = persisted("execute_all_god_mode", false);
  const persistedDebugEvents = persisted("execute_all_debug_events", false);
  const persistedRequestTimeout = persisted("execute_all_request_timeout", null);
  const persistedIgnoreWarmupErrors = persisted("execute_all_ignore_warmup_errors", false);
  const persistedSkipHealthChecks = persisted("execute_all_skip_health_checks", false);
  const persistedEntrypoint = persisted("execute_all_entrypoint", "");

  // Initialize from persisted stores
  let userTag = $persistedUserTag;
  let deviceSelections = $persistedDeviceSelections; // { deviceId: { enabled: boolean, serial_numbers: string[] } }
  let buildId = $persistedBuildId;
  let godMode = $persistedGodMode;
  let debugEvents = $persistedDebugEvents;
  let requestTimeout = $persistedRequestTimeout;
  let ignoreWarmupErrors = $persistedIgnoreWarmupErrors;
  let skipHealthChecks = $persistedSkipHealthChecks;
  let selectedEntrypoint = $persistedEntrypoint;

  let sidecarsByDevice = {}; // Maps device ID to { deviceName, sidecars }
  let expandedDevices = {}; // Track which devices are expanded

  const dispatch = createEventDispatcher();

  // Computed values from deviceSelections
  $: selectedDevices = Object.keys(deviceSelections).filter(
    (deviceId) => deviceSelections[deviceId]?.enabled,
  );
  $: selectedSidecars = selectedDevices.flatMap(
    (deviceId) => deviceSelections[deviceId]?.serial_numbers || [],
  );

  // Filter deviceSelections against actual available devices when devices list loads
  $: if (devices && devices.length > 0 && Object.keys(deviceSelections).length > 0) {
    const availableDeviceIds = new Set(devices.map((d) => d.value));
    const filteredSelections = {};
    let hasChanges = false;

    for (const [deviceId, selection] of Object.entries(deviceSelections)) {
      if (availableDeviceIds.has(deviceId)) {
        filteredSelections[deviceId] = selection;
      } else {
        hasChanges = true;
      }
    }

    if (hasChanges) {
      deviceSelections = filteredSelections;
    }
  }

  // Sync local state to persisted stores
  $: persistedUserTag.set(userTag);
  $: persistedDeviceSelections.set(deviceSelections);
  $: persistedBuildId.set(buildId);
  $: persistedGodMode.set(godMode);
  $: persistedDebugEvents.set(debugEvents);
  $: persistedRequestTimeout.set(requestTimeout);
  $: persistedIgnoreWarmupErrors.set(ignoreWarmupErrors);
  $: persistedSkipHealthChecks.set(skipHealthChecks);
  $: persistedEntrypoint.set(selectedEntrypoint);

  $: entrypointOptions = [
    { value: "", label: "All workflows" },
    ...(Array.isArray($availableEntrypoints) ? $availableEntrypoints : []),
  ];

  function handleTimeoutChange(event) {
    requestTimeout = event.detail;
  }

  function executeAll() {
    execution.triggerExecuteAll({
      soak: 1,
      godMode,
      debugEvents,
      userTag,
      deviceFilter: selectedDevices,
      requestTimeout,
      buildId,
      sidecarFilter: selectedSidecars,
      ignoreWarmupErrors,
      skipHealthChecks,
      entrypoint: selectedEntrypoint || null,
    });
  }

  async function onOpen() {
    dispatch("open", {});
    dispatch("updateEntrypoints", { onlyEntrypoints: true });

    // Load sidecars for selected devices if in developer mode
    if (selectedDevices.length > 0) {
      await loadSidecarsForDevices();
    }
  }

  async function loadSidecarsForDevices() {
    if (!selectedDevices || selectedDevices.length === 0) {
      sidecarsByDevice = {};
      expandedDevices = {};
      return;
    }

    // Load sidecars for all selected devices and group by device
    const grouped = {};
    const validSerialNumbersByDevice = {}; // Track valid serial numbers per device

    for (const deviceId of selectedDevices) {
      try {
        const { sidecars } = await $liveStore.pushEvent("request_sidecars", {
          payload: { selectedDevice: deviceId },
        });
        if (sidecars && sidecars.length > 0) {
          // Find device name from devices list
          const device = devices.find((d) => d.value === deviceId);
          const deviceName = device ? device.label : "Unknown Device";

          grouped[deviceId] = {
            deviceName,
            sidecars,
          };

          // Track valid serial numbers for this device
          validSerialNumbersByDevice[deviceId] = new Set(sidecars.map((s) => s.serial_number));
        }
      } catch (error) {
        console.error("Failed to load sidecars for device:", deviceId, error);
      }
    }
    sidecarsByDevice = grouped;

    // Filter out serial numbers that don't exist in the actual sidecar list for each device
    let hasChanges = false;
    const updatedSelections = { ...deviceSelections };

    for (const deviceId of selectedDevices) {
      const selection = updatedSelections[deviceId];
      if (selection && selection.serial_numbers && selection.serial_numbers.length > 0) {
        const validSerialNumbers = validSerialNumbersByDevice[deviceId];
        if (validSerialNumbers) {
          const filteredSerialNumbers = selection.serial_numbers.filter((sn) =>
            validSerialNumbers.has(sn),
          );

          if (filteredSerialNumbers.length !== selection.serial_numbers.length) {
            updatedSelections[deviceId] = {
              ...selection,
              serial_numbers: filteredSerialNumbers,
            };
            hasChanges = true;
          }
        }
      }
    }

    if (hasChanges) {
      deviceSelections = updatedSelections;
    }
  }

  function toggleDeviceExpanded(deviceId) {
    expandedDevices[deviceId] = !expandedDevices[deviceId];
    expandedDevices = { ...expandedDevices }; // Trigger reactivity
  }

  function onResetSidecarFilter() {
    const updatedSelections = { ...deviceSelections };
    for (const deviceId of selectedDevices) {
      if (updatedSelections[deviceId]) {
        updatedSelections[deviceId] = {
          ...updatedSelections[deviceId],
          serial_numbers: [],
        };
      }
    }
    deviceSelections = updatedSelections;
  }

  function onSidecarFilterChange(event, deviceId) {
    const checkbox = event.target;
    const serialNumber = checkbox.value;

    const updatedSelections = { ...deviceSelections };
    const selection = updatedSelections[deviceId] || { enabled: true, serial_numbers: [] };
    const currentSerialNumbers = selection.serial_numbers || [];

    if (checkbox.checked) {
      updatedSelections[deviceId] = {
        ...selection,
        serial_numbers: [...currentSerialNumbers, serialNumber],
      };
    } else {
      updatedSelections[deviceId] = {
        ...selection,
        serial_numbers: currentSerialNumbers.filter((sn) => sn !== serialNumber),
      };
    }

    deviceSelections = updatedSelections;
  }

  // Reload sidecars when devices change
  $: if (selectedDevices) {
    loadSidecarsForDevices();
  }
</script>

<DropdownMenu buttonText="Execute All" {onOpen} width="800px">
  <div slot="prefix" class="flex items-center">
    <Icon src={PaperAirplane} class="w-4 h-4 mr-1" />
  </div>

  <div slot="content" let:close>
    <h3 class="text-lg font-semibold text-content mb-1">Execute All Targets</h3>
    <p class="text-xs text-content-muted mb-4">
      Are you sure you want to execute <span class="font-semibold">
        {#if selectedSidecars.length > 0}
          {apps.length * selectedSidecars.length}
        {:else}
          {apps.length * selectedDevices.length}
        {/if}
      </span>
      runs? <span class="text-content-subtle">Edit in settings tab</span>
    </p>

    {#if devices}
      <div class="mb-4">
        <label class="block text-xs font-medium text-content mb-2">
          Select Target Devices
          {#if selectedSidecars.length > 0}
            <button
              type="button"
              on:click={onResetSidecarFilter}
              class="ml-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear Serial Filters
            </button>
          {/if}
        </label>

        <div
          class="border border-line-strong rounded-lg overflow-hidden bg-surface shadow-sm max-h-80 overflow-y-auto"
        >
          {#each devices as device}
            {@const deviceSelection = deviceSelections[device.value]}
            {@const isSelected = deviceSelection?.enabled ?? false}
            {@const deviceSerialNumbers = deviceSelection?.serial_numbers || []}
            {@const deviceSidecars = sidecarsByDevice[device.value]?.sidecars || []}
            {@const hasSelectedSidecars = deviceSerialNumbers.length > 0}
            {@const isExpanded = expandedDevices[device.value]}

            <div class="border-b border-line-strong last:border-b-0">
              <!-- Device Row -->
              <button
                type="button"
                on:click={() => {
                  if (isSelected && deviceSidecars.length > 0) {
                    toggleDeviceExpanded(device.value);
                  }
                }}
                class="w-full flex items-center hover:bg-surface-hover transition-colors px-4 py-3 {isSelected &&
                deviceSidecars.length > 0
                  ? 'cursor-pointer'
                  : ''}"
                disabled={!(isSelected && deviceSidecars.length > 0)}
              >
                <input
                  type="checkbox"
                  value={device.value}
                  checked={isSelected}
                  on:click={(e) => e.stopPropagation()}
                  on:change={(e) => {
                    const updatedSelections = { ...deviceSelections };
                    if (e.target.checked) {
                      updatedSelections[device.value] = {
                        enabled: true,
                        serial_numbers: deviceSelection?.serial_numbers || [],
                      };
                    } else {
                      if (updatedSelections[device.value]) {
                        updatedSelections[device.value] = {
                          ...updatedSelections[device.value],
                          enabled: false,
                        };
                      }
                    }
                    deviceSelections = updatedSelections;
                  }}
                  class="h-4 w-4 text-blue-600 border-line-strong rounded focus:ring-2 focus:ring-blue-500 mr-3 cursor-pointer flex-shrink-0"
                />

                <div class="flex items-center gap-2.5 flex-1 min-w-0">
                  {#if device.image}
                    <img src={device.image} alt={device.label} class="w-5 h-5 flex-shrink-0" />
                  {/if}
                  <div class="flex flex-col items-start min-w-0">
                    <span class="text-sm font-medium text-content">{device.label}</span>
                    {#if isSelected && deviceSidecars.length > 0}
                      <span
                        class="text-xs {hasSelectedSidecars
                          ? 'text-blue-600'
                          : 'text-content-subtle'}"
                      >
                        {deviceSerialNumbers.length} out of {deviceSidecars.length} selected
                      </span>
                    {/if}
                  </div>
                </div>

                {#if isSelected && deviceSidecars.length > 0}
                  <svg
                    class="w-4 h-4 text-content-subtle transition-transform flex-shrink-0 {isExpanded
                      ? 'rotate-90'
                      : ''}"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                {/if}
              </button>

              <!-- Sidecars (Nested) -->
              {#if isSelected && isExpanded && deviceSidecars.length > 0}
                <div class="bg-surface-sunken px-4 py-2 border-t border-line-strong">
                  <div class="pl-7 space-y-0.5">
                    {#each deviceSidecars as sidecar}
                      <label
                        class="flex items-center py-1.5 px-2 rounded hover:bg-surface cursor-pointer transition-colors group"
                      >
                        <input
                          type="checkbox"
                          value={sidecar.serial_number}
                          checked={deviceSerialNumbers.includes(sidecar.serial_number)}
                          on:change={(e) => onSidecarFilterChange(e, device.value)}
                          class="h-3.5 w-3.5 text-blue-600 border-line-strong rounded focus:ring-2 focus:ring-blue-500 mr-2.5"
                        />
                        <span class="text-xs text-content group-hover:text-content font-mono">
                          {sidecar.serial_number}
                        </span>
                        {#if sidecar.pool}
                          <span
                            class="text-xs text-content-subtle ml-2 bg-surface-muted px-1.5 py-0.5 rounded"
                          >
                            {typeof sidecar.pool === "object" && sidecar.pool != null
                              ? sidecar.pool.name
                              : sidecar.pool}
                          </span>
                        {/if}
                      </label>
                    {/each}
                  </div>
                </div>
              {/if}
            </div>
          {/each}
        </div>

        {#if selectedSidecars.length > 0}
          <p class="text-xs text-content-muted mt-2 flex items-start gap-1">
            <svg
              class="w-3 h-3 text-blue-500 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clip-rule="evenodd"
              />
            </svg>
            <span>
              Serial filter creates <strong>{selectedSidecars.length}</strong>
              request{selectedSidecars.length !== 1 ? "s" : ""}
            </span>
          </p>
        {/if}
      </div>
    {/if}

    <div class="mb-3">
      <SelectInput
        bind:value={selectedEntrypoint}
        optional
        label="Entrypoint"
        options={entrypointOptions}
        variant="wide"
      />
    </div>

    <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-2.5 mb-3">
      <p class="text-xs mb-1.5 text-yellow-700 font-medium">Developer options:</p>

      <div class="space-y-1 text-xs">
        <CheckboxInput
          value={godMode}
          on:input={(e) => (godMode = e.detail)}
          label="Run in God Mode"
          class="text-xs"
        />
        <CheckboxInput
          value={ignoreWarmupErrors}
          on:input={(e) => (ignoreWarmupErrors = e.detail)}
          label="Ignore Warmup Errors"
          class="text-xs"
        />
        <CheckboxInput
          value={skipHealthChecks}
          on:input={(e) => (skipHealthChecks = e.detail)}
          label="Skip Healthchecks"
          class="text-xs"
        />
        <CheckboxInput
          value={debugEvents}
          on:input={(e) => (debugEvents = e.detail)}
          label="Verbose Logging"
          class="text-xs"
        />
      </div>
    </div>

    <div class="space-y-3 mb-3">
      <div>
        <TextInput bind:value={userTag} label="User Metric Tag" optional />
      </div>
      <div>
        <DurationInput
          value={requestTimeout}
          on:input={handleTimeoutChange}
          label="Request Timeout"
          defaultUnit="minutes"
          optional
        />
      </div>
    </div>

    <div class="mb-3">
      <TextInput bind:value={buildId} optional trim label="Build ID (optional)" />
    </div>

    <div class="flex justify-end space-x-2">
      <Button variant="secondary" on:click={close}>Cancel</Button>

      <Button
        icon={PaperAirplane}
        on:click={() => {
          executeAll();
          close();
        }}
      >
        Execute
      </Button>
    </div>
  </div>
</DropdownMenu>
